import { serverOrigin } from "../platform";
import { ExportChat } from "./Export";
import { ReportButton } from "./Report";
import { useEffect, useLayoutEffect, useState } from "preact/hooks";
import { api, ApiError, type Group, type GroupMember, type InvitePreview } from "../api";
import type { Msg } from "../db";
import * as S from "../state";
import { phoneOf, timeOf } from "../util";
import { Avatar, AvatarPicker, uploadAvatar } from "./Avatar";
import { Icon } from "./Icon";
import { drawer, engine, infoOpen, toast } from "./Main";
import { Confirm, TimerSelect } from "./Panels";

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

function useEscape(fn: () => void) {
  useLayoutEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && fn();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
}

// ---- pick contacts (new group / add participants) --------------------------------

type Updater = (fn: (prev: string[]) => string[]) => void;

/** Multi-select contacts. `onChange` takes an updater so rapid clicks never
 * work from a stale selection. */
export function ContactPicker({ exclude = [], picked, onChange }: { exclude?: string[]; picked: string[]; onChange: Updater }) {
  const toggle = (j: string) => onChange((prev) => (prev.includes(j) ? prev.filter((x) => x !== j) : [...prev, j]));
  const [q, setQ] = useState("");
  const f = q.trim().toLowerCase();
  const list = S.contacts.value
    .filter((c) => !exclude.includes(c.jid))
    .filter((c) => !f || c.name.toLowerCase().includes(f) || c.jid.includes(f.replace(/\D/g, "") || "\u0000"))
    .sort((a, b) => a.name.localeCompare(b.name));
  return (
    <>
      {picked.length > 0 && (
        <div class="picked-chips">
          {picked.map((j) => (
            <button key={j} class="picked-chip" onClick={() => toggle(j)} aria-label={`Remove ${S.displayName(j)}`}>
              <Avatar url={S.profiles.value.get(j)?.avatar_url} jid={j} name={S.displayName(j)} size={24} />
              {S.displayName(j)}
              <Icon name="close" size={14} />
            </button>
          ))}
        </div>
      )}
      <div class="search">
        <Icon name="search" size={20} />
        <input autoFocus value={q} onInput={(e) => setQ(e.currentTarget.value)} placeholder="Search name or number" aria-label="Search contacts" />
      </div>
      <ul class="pick-list">
        {list.map((c) => {
          const on = picked.includes(c.jid);
          return (
            <li key={c.jid} onClick={() => toggle(c.jid)}>
              <span class={"check" + (on ? " on" : "")}>{on && <Icon name="check" size={16} />}</span>
              <Avatar url={S.profiles.value.get(c.jid)?.avatar_url} jid={c.jid} name={c.name} size={40} />
              <div class="grow">
                <div class="chat-name">{c.name}</div>
                <div class="muted small ellipsis">{S.profiles.value.get(c.jid)?.about ?? phoneOf(c.jid)}</div>
              </div>
            </li>
          );
        })}
        {!list.length && <li class="empty">{f ? "No contacts found" : "No contacts to add. Save contacts first."}</li>}
      </ul>
    </>
  );
}

export function NewGroup() {
  const [step, setStep] = useState<"members" | "details">("members");
  const [picked, setPicked] = useState<string[]>([]);
  const [name, setName] = useState("");
  const [icon, setIcon] = useState<{ id: string; url: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const back = () => (step === "details" ? setStep("members") : (drawer.value = "none"));
  useEscape(back);

  async function create() {
    setBusy(true);
    try {
      await engine.value!.createGroup(name.trim(), picked, icon?.id);
      drawer.value = "none";
    } catch (e) {
      toast(errText(e));
      setBusy(false);
    }
  }

  return (
    <div class="drawer" role="dialog" aria-label="New group">
      <header class="drawer-head">
        <button class="icon-btn" onClick={back} aria-label="Back">
          <Icon name="back" />
        </button>
        <h2>{step === "members" ? "Add group participants" : "New group"}</h2>
      </header>
      <div class="drawer-body">
        {step === "members" ? (
          <ContactPicker picked={picked} onChange={setPicked} />
        ) : (
          <div class="new-group-details">
            <label class="group-icon-pick">
              <Avatar url={icon?.url} jid="new-group" name={name} size={160} group />
              <span class="avatar-picker-overlay">
                <Icon name="camera" size={24} />
                Add group icon
              </span>
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const f = e.currentTarget.files?.[0];
                  if (f) void uploadAvatar(f).then((u) => setIcon({ id: u.id, url: u.url }), (err) => toast(errText(err)));
                }}
              />
            </label>
            <input
              autoFocus
              class="line-input"
              value={name}
              maxLength={100}
              onInput={(e) => setName(e.currentTarget.value)}
              onKeyDown={(e) => e.key === "Enter" && name.trim() && !busy && void create()}
              placeholder="Group subject"
              aria-label="Group name"
            />
            <p class="muted small">{picked.length} participant{picked.length === 1 ? "" : "s"} + you</p>
          </div>
        )}
      </div>
      <div class="drawer-fab">
        {step === "members"
          ? picked.length > 0 && (
              <button class="icon-btn send-fab" onClick={() => setStep("details")} aria-label="Next">
                <Icon name="arrowRight" />
              </button>
            )
          : name.trim() && (
              <button class="icon-btn send-fab" disabled={busy} onClick={() => void create()} aria-label="Create group">
                <Icon name="check" />
              </button>
            )}
      </div>
    </div>
  );
}

// ---- group info (right panel) ------------------------------------------------------

export function GroupInfo({ jid }: { jid: string }) {
  const g = S.groups.value.get(jid);
  const me = S.myJid.value;
  const [adding, setAdding] = useState(false);
  const [confirmExit, setConfirmExit] = useState(false);
  const [editing, setEditing] = useState<"name" | "description" | null>(null);
  const [draft, setDraft] = useState("");
  if (!g) return null;
  const isAdmin = g.my_role === "owner" || g.my_role === "admin";
  const canEdit = !g.left && (!g.only_admins_edit || isAdmin);
  const inviteURL = g.invite_code ? `${serverOrigin()}/join/${g.invite_code}` : "";

  async function run(fn: () => Promise<Group | void>) {
    try {
      const res = await fn();
      if (res) engine.value?.applyGroup(res);
    } catch (e) {
      toast(errText(e));
    }
  }

  function saveField() {
    const v = draft.trim();
    const field = editing;
    setEditing(null);
    if (!field || v === (field === "name" ? g!.name : g!.description) || (field === "name" && !v)) return;
    void run(() => api.patchGroup(jid, { [field]: v }));
  }

  const members = [...g.members].sort((a, b) => {
    if (a.jid === me) return -1;
    if (b.jid === me) return 1;
    const ra = a.role === "member" ? 1 : 0;
    const rb = b.role === "member" ? 1 : 0;
    return ra - rb || S.memberName(a.jid).localeCompare(S.memberName(b.jid));
  });

  return (
    <aside class="info" aria-label="Group info">
      <header class="drawer-head">
        <button class="icon-btn" onClick={() => (infoOpen.value = false)} aria-label="Close">
          <Icon name="close" />
        </button>
        <h2>Group info</h2>
      </header>
      <div class="drawer-body">
        <div class="info-hero">
          <AvatarPicker
            url={g.avatar_url}
            jid={jid}
            name={g.name}
            group
            disabled={!canEdit}
            onChange={() => {}}
            save={async (id) => {
              const res = await api.patchGroup(jid, { avatar_media_id: id });
              engine.value?.applyGroup(res);
              return res.avatar_url;
            }}
          />
          {editing === "name" ? (
            <input
              class="line-input center"
              autoFocus
              value={draft}
              maxLength={100}
              onInput={(e) => setDraft(e.currentTarget.value)}
              onBlur={saveField}
              onKeyDown={(e) => e.key === "Enter" && saveField()}
              aria-label="Group name"
            />
          ) : (
            <h3 class="editable" onClick={() => canEdit && (setDraft(g.name), setEditing("name"))}>
              {g.name} {canEdit && <Icon name="edit" size={18} />}
            </h3>
          )}
          <p class="muted">Group · {g.members.length} participants</p>
        </div>

        <div class="field">
          <label>Description</label>
          {editing === "description" ? (
            <textarea
              class="line-input"
              autoFocus
              rows={3}
              value={draft}
              maxLength={2048}
              onInput={(e) => setDraft(e.currentTarget.value)}
              onBlur={saveField}
              aria-label="Group description"
            />
          ) : (
            <div class="field-value">
              <span class="pre">{g.description || (canEdit ? "Add group description" : "No description")}</span>
              {canEdit && (
                <button class="icon-btn" onClick={() => (setDraft(g.description), setEditing("description"))} aria-label="Edit description">
                  <Icon name="edit" size={20} />
                </button>
              )}
            </div>
          )}
        </div>

        {!g.left && <TimerSelect jid={jid} disabled={!canEdit} />}

        {isAdmin && !g.left && (
          <div class="field">
            <label>Group settings</label>
            <label class="toggle">
              <span>
                <strong>Send messages</strong>
                <small class="muted">{g.only_admins_send ? "Only admins" : "All participants"}</small>
              </span>
              <input type="checkbox" checked={g.only_admins_send} onChange={(e) => void run(() => api.patchGroup(jid, { only_admins_send: e.currentTarget.checked }))} aria-label="Only admins can send messages" />
            </label>
            <label class="toggle">
              <span>
                <strong>Edit group info</strong>
                <small class="muted">{g.only_admins_edit ? "Only admins" : "All participants"}</small>
              </span>
              <input type="checkbox" checked={g.only_admins_edit} onChange={(e) => void run(() => api.patchGroup(jid, { only_admins_edit: e.currentTarget.checked }))} aria-label="Only admins can edit group info" />
            </label>
            <label class="toggle">
              <span>
                <strong>Share recent messages with new participants</strong>
                <small class="muted">{g.share_history ? "New participants see the last 14 days (up to 100 messages)" : "New participants only see messages sent after they join"}</small>
              </span>
              <input type="checkbox" checked={!!g.share_history} onChange={(e) => void run(() => api.patchGroup(jid, { share_history: e.currentTarget.checked }))} aria-label="Share recent messages with new participants" />
            </label>
            <label class="toggle">
              <span>
                <strong>Approve new participants</strong>
                <small class="muted">{g.approve_joins ? "People joining by invite link need an admin's approval" : "Anyone with the invite link can join"}</small>
              </span>
              <input type="checkbox" checked={!!g.approve_joins} onChange={(e) => void run(() => api.patchGroup(jid, { approve_joins: e.currentTarget.checked }))} aria-label="Approve new participants" />
            </label>
          </div>
        )}

        {isAdmin && !g.left && (
          <div class="field">
            <label>Invite via link</label>
            {g.invite_code ? (
              <>
                <div class="invite-link">
                  <Icon name="link" />
                  <code class="ellipsis">{inviteURL}</code>
                </div>
                <div class="row-buttons left">
                  <button class="link" onClick={() => void navigator.clipboard?.writeText(inviteURL).then(() => toast("Link copied"))}>
                    <Icon name="copy" size={16} /> Copy link
                  </button>
                  <button class="link" onClick={() => void run(() => api.resetInvite(jid))}>
                    <Icon name="refresh" size={16} /> Reset
                  </button>
                  <button class="link danger" onClick={() => void run(() => api.revokeInvite(jid))}>
                    Revoke
                  </button>
                </div>
              </>
            ) : (
              <button class="list-action" onClick={() => void run(() => api.resetInvite(jid))}>
                <span class="list-action-icon">
                  <Icon name="link" />
                </span>
                Create invite link
              </button>
            )}
          </div>
        )}

        {isAdmin && !g.left && <JoinRequests jid={jid} />}
        <div class="info-actions">
          <h3 class="section">{g.members.length} participants</h3>
          {isAdmin && !g.left && (
            <button class="list-action" onClick={() => setAdding(true)}>
              <span class="list-action-icon">
                <Icon name="personAdd" />
              </span>
              Add participants
            </button>
          )}
          <ul class="member-list">
            {members.map((m) => (
              <MemberRow key={m.jid} room={jid} m={m} canManage={isAdmin && !g.left && m.jid !== me && m.role !== "owner"} onChange={run} />
            ))}
          </ul>
        </div>

        <div class="info-actions">
          <ExportChat jid={jid} />
          {!g.left && <ReportButton jid={jid} />}
          {g.left ? (
            <>
              <p class="muted pad">You're no longer a participant in this group.</p>
              <button
                class="list-action danger"
                onClick={() => {
                  infoOpen.value = false;
                  void engine.value?.deleteChat(jid);
                }}
              >
                <span class="list-action-icon">
                  <Icon name="trash" />
                </span>
                Delete group
              </button>
            </>
          ) : (
            <button class="list-action danger" onClick={() => setConfirmExit(true)}>
              <span class="list-action-icon">
                <Icon name="exit" />
              </span>
              Exit group
            </button>
          )}
        </div>
      </div>
      {adding && <AddParticipants room={jid} onClose={() => setAdding(false)} />}
      {confirmExit && (
        <Confirm
          text={`Exit "${g.name}" group?`}
          action="Exit group"
          onCancel={() => setConfirmExit(false)}
          onConfirm={() => {
            setConfirmExit(false);
            void run(async () => {
              await api.removeGroupMember(jid, me);
              await engine.value?.loadGroup(jid);
            });
          }}
        />
      )}
    </aside>
  );
}

function MemberRow({ room, m, canManage, onChange }: { room: string; m: GroupMember; canManage: boolean; onChange: (fn: () => Promise<Group | void>) => Promise<void> }) {
  const [menu, setMenu] = useState(false);
  const me = S.myJid.value;
  const p = S.profiles.value.get(m.jid);
  const name = S.memberName(m.jid);
  const avatarName = m.jid === me ? (S.profiles.value.get(me)?.name ?? name) : name;
  return (
    <li class="member-row" onClick={() => m.jid !== me && setMenu(!menu)}>
      <Avatar url={p?.avatar_url} jid={m.jid} name={avatarName} size={40} />
      <div class="grow">
        <div class="chat-name">{name}</div>
        <div class="muted small ellipsis">{p?.about ?? phoneOf(m.jid)}</div>
      </div>
      {m.role !== "member" && <span class="tag">Group admin</span>}
      {menu && (
        <ul class="menu member-menu" role="menu" onClick={(e) => e.stopPropagation()}>
          <li
            role="menuitem"
            onClick={() => {
              setMenu(false);
              infoOpen.value = false;
              void engine.value?.openChat(m.jid);
            }}
          >
            Message {name}
          </li>
          {canManage && (
            <li role="menuitem" onClick={() => (setMenu(false), void onChange(() => api.setGroupRole(room, m.jid, m.role === "admin" ? "member" : "admin")))}>
              {m.role === "admin" ? "Dismiss as admin" : "Make group admin"}
            </li>
          )}
          {canManage && (
            <li
              role="menuitem"
              class="danger"
              onClick={() => {
                setMenu(false);
                void onChange(async () => {
                  await api.removeGroupMember(room, m.jid);
                  await engine.value?.loadGroup(room);
                });
              }}
            >
              Remove {name}
            </li>
          )}
        </ul>
      )}
    </li>
  );
}

function AddParticipants({ room, onClose }: { room: string; onClose: () => void }) {
  const g = S.groups.value.get(room)!;
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  useEscape(onClose);
  async function add() {
    setBusy(true);
    try {
      const g = await api.addGroupMembers(room, picked);
      engine.value?.applyGroup(g);
      const added = picked.filter((j) => !g.skipped?.includes(j));
      void engine.value?.shareGroupHistory(room, added).catch((e) => console.warn("group history", e));
      if (g.skipped?.length) toast(`${g.skipped.map((j) => S.displayName(j)).join(", ")} couldn't be added because of their privacy settings. Send them an invite link.`);
      onClose();
    } catch (e) {
      toast(errText(e));
      setBusy(false);
    }
  }
  return (
    <div class="modal-backdrop" onClick={onClose}>
      <div class="modal forward" role="dialog" aria-label="Add participants" onClick={(e) => e.stopPropagation()}>
        <header>
          <button class="icon-btn" onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
          <h2>Add participants</h2>
        </header>
        <ContactPicker exclude={g.members.map((m) => m.jid)} picked={picked} onChange={setPicked} />
        {picked.length > 0 && (
          <footer class="forward-footer">
            <span class="ellipsis">{picked.map((j) => S.displayName(j)).join(", ")}</span>
            <button class="icon-btn send-fab" disabled={busy} onClick={() => void add()} aria-label="Add">
              <Icon name="check" />
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}

// ---- join by invite link -------------------------------------------------------------

export function JoinDialog({ code, onClose }: { code: string; onClose: () => void }) {
  const [info, setInfo] = useState<InvitePreview | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEscape(onClose);
  useEffect(() => {
    api.invite(code).then(setInfo, (e) => setError(e instanceof ApiError && e.status === 404 ? "This invite link is invalid or was reset." : errText(e)));
  }, [code]);

  const [requested, setRequested] = useState(false);
  async function join() {
    setBusy(true);
    try {
      const g = await api.joinInvite(code);
      // The admins approve new participants: wait for them.
      if ("requested" in g) return (setRequested(true), setBusy(false));
      engine.value?.applyGroup(g);
      void engine.value?.requestGroupHistory(g.jid).catch((e) => console.warn("group history", e));
      onClose();
      await engine.value?.openChat(g.jid);
    } catch (e) {
      setError(errText(e));
      setBusy(false);
    }
  }

  return (
    <div class="modal-backdrop" onClick={onClose}>
      <div class="modal join" role="dialog" aria-label="Join group" onClick={(e) => e.stopPropagation()}>
        {error ? (
          <p class="error">{error}</p>
        ) : !info ? (
          <p class="muted">Loading invite…</p>
        ) : (
          <div class="join-body">
            <Avatar url={info.avatar_url} jid={info.jid} name={info.name} size={96} group />
            <h2>{info.name}</h2>
            <p class="muted">Group · {info.member_count} participants</p>
            {info.description && <p class="pre">{info.description}</p>}
            {(requested || info.requested) && !info.is_member && (
              <p class="join-requested" role="status">
                Request sent. An admin of {info.name} needs to approve it; you'll see the group when they do.
              </p>
            )}
          </div>
        )}
        <div class="row-buttons">
          <button class="link" onClick={onClose}>
            {info?.is_member ? "Close" : "Cancel"}
          </button>
          {info && !info.is_member && !requested && !info.requested && (
            <button class="primary" disabled={busy} onClick={() => void join()}>
              {info.approve_joins ? "Request to join" : "Join group"}
            </button>
          )}
          {info?.is_member && (
            <button class="primary" onClick={() => (onClose(), void engine.value?.openChat(info.jid))}>
              Open group
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- message info (who got / read my message) -------------------------------------

export function MessageInfo({ m, onClose }: { m: Msg; onClose: () => void }) {
  useEscape(onClose);
  const g = S.groups.value.get(m.chat);
  const me = S.myJid.value;
  const members = g?.members.filter((x) => x.jid !== me && x.joined_at * 1000 <= m.ts + 60_000) ?? [];
  const rc = m.receipts ?? {};
  const read = members.filter((x) => rc[x.jid]?.r);
  const delivered = members.filter((x) => !rc[x.jid]?.r && rc[x.jid]?.d);
  const pending = members.filter((x) => !rc[x.jid]?.r && !rc[x.jid]?.d);
  const row = (x: GroupMember, t?: number) => (
    <li key={x.jid}>
      <Avatar url={S.profiles.value.get(x.jid)?.avatar_url} jid={x.jid} name={S.memberName(x.jid)} size={36} />
      <span class="grow">{S.memberName(x.jid)}</span>
      {t && <small class="muted">{timeOf(t)}</small>}
    </li>
  );
  return (
    <div class="modal-backdrop" onClick={onClose}>
      <div class="modal forward" role="dialog" aria-label="Message info" onClick={(e) => e.stopPropagation()}>
        <header>
          <button class="icon-btn" onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
          <h2>Message info</h2>
        </header>
        <div class="msg-info">
          <h3 class="section read">Read by</h3>
          <ul>{read.length ? read.map((x) => row(x, rc[x.jid]?.r)) : <li class="muted">Nobody yet</li>}</ul>
          <h3 class="section">Delivered to</h3>
          <ul>{delivered.length ? delivered.map((x) => row(x, rc[x.jid]?.d)) : <li class="muted">—</li>}</ul>
          {pending.length > 0 && (
            <>
              <h3 class="section">Not delivered yet</h3>
              <ul>{pending.map((x) => row(x))}</ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Admins: people asking to join (groups that approve new participants). */
function JoinRequests({ jid }: { jid: string }) {
  const [list, setList] = useState<{ jid: string; name: string; requested_at: number }[]>([]);
  const changed = S.joinRequestsChanged.value;
  const load = () => void api.joinRequests(jid).then((r) => setList(r.requests), () => setList([]));
  useEffect(load, [jid, changed]);
  if (!list.length) return null;
  const answer = (user: string, approve: boolean) =>
    void api.answerJoinRequest(jid, user, approve).then(
      () => (setList((l) => l.filter((x) => x.jid !== user)), toast(approve ? "Approved" : "Declined"), approve && void engine.value?.loadGroups()),
      (e) => toast(errText(e)),
    );
  return (
    <div class="info-actions join-requests">
      <h3 class="section">Pending requests ({list.length})</h3>
      <ul class="member-list">
        {list.map((r) => (
          <li key={r.jid} class="member-row">
            <Avatar url={S.profiles.value.get(r.jid)?.avatar_url} jid={r.jid} name={r.name} size={40} />
            <span class="grow">
              <span class="chat-name">{r.name}</span>
              <small class="muted">asked {new Date(r.requested_at * 1000).toLocaleString()}</small>
            </span>
            <button class="chip" onClick={() => answer(r.jid, false)} aria-label={`Decline ${r.name}`}>
              Decline
            </button>
            <button class="chip on" onClick={() => answer(r.jid, true)} aria-label={`Approve ${r.name}`}>
              Approve
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
